import { Fragment, memo } from 'react';
import { Lock } from '@mui/icons-material';
import { Box, Stack } from '@mui/material';
import type { Outcome } from '../../@types';
import { useOddsChangeIndicator } from '../../hooks/useOddsChangeIndicator';
import useOddsFormat from '../../hooks/useOddsFormat';
import { useSkinStyles } from '../../hooks/useSkinStyles';
import {
  getOutcomeTableCellSkinStyles,
  type OutcomeTableCellSkinStyles,
} from '../../styles/components/outcomeTableCell';
import OddsDisplay from './OddsDisplay';

import { mergeSx } from '../../utils/styles';

type OutcomeTableCellOddsProps = {
  outcome: Outcome;
  selected: boolean;
  interactionDisabled: boolean;
  searchVariant?: boolean;
  mixedMarket?: boolean;
  featuredCardVariant?: boolean;
};

const OutcomeTableCellOdds = ({
  outcome,
  selected,
  interactionDisabled,
  searchVariant,
  mixedMarket,
  featuredCardVariant,
}: OutcomeTableCellOddsProps) => {
  const { formatOddsValue } = useOddsFormat();
  const latestChanges = outcome.latestChanges;
  const prevOdds = latestChanges?.prevOdds;
  const hasPreviousOdds = prevOdds != null;
  const canAnimateOddsChange = !interactionDisabled && !!outcome.isActive && hasPreviousOdds;

  const { direction, indicatorStyles, isFlashing } = useOddsChangeIndicator({
    canFlash: canAnimateOddsChange,
    prevOdds,
    changedAt: latestChanges?.changedAt,
    currentOdds: outcome.odds,
    selected,
  });

  const styles = useSkinStyles<OutcomeTableCellSkinStyles>(
    getOutcomeTableCellSkinStyles(selected, interactionDisabled, searchVariant, mixedMarket, featuredCardVariant)
  );

  if (interactionDisabled) {
    return (
      <Stack
        sx={{
          alignItems: 'center',
        }}
      >
        <Lock sx={styles.lockIcon} />
      </Stack>
    );
  }

  const shouldAnimateOddsChange = isFlashing && !!direction && prevOdds != null;

  if (shouldAnimateOddsChange) {
    return (
      <Fragment key={latestChanges?.changedAt}>
        <Box component='span' sx={indicatorStyles.triangle} />
        <OddsDisplay
          value={outcome.odds}
          id={outcome.id}
          data-current-odds={formatOddsValue(outcome.odds)}
          data-prev-odds={formatOddsValue(prevOdds)}
          sx={mergeSx(
            {
              color: styles.oddsColor,
            },
            indicatorStyles.odds
          )}
        />
      </Fragment>
    );
  }

  return (
    <>
      <Box component='span' sx={indicatorStyles.triangle} />
      <OddsDisplay
        value={outcome.odds}
        id={outcome.id}
        sx={{
          color: styles.oddsColor,
        }}
      />
    </>
  );
};

export default memo(OutcomeTableCellOdds);
